/**
 * @author        woody@zumper.com (Woody Anderson)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Base logic for Agent.
 */

import { mediaUrl, toTelHref } from '@zcode/nr-utils'
import type {
  Agent as AgentType,
  AgentWithProfile,
  AgentWithoutProfile,
  RawAgent,
} from './agent.types'
import { Bizo, autoclass } from './base'

export const Agent = autoclass<AgentType, RawAgent>(
  'Agent',
  class Agent extends Bizo<AgentType, RawAgent> {},
  {
    id: 'user_id',
    applicationFee: 'application_fee',
    backgroundUrl: 'background_url',
    colors: 'colors',
    email: 'email',
    firstName: 'first_name',
    hasProfile: 'has_profile',
    isZpro: 'is_zpro',
    lastName: 'last_name',
    logoUrl: 'logo_url',
    longDescription: 'long_description',
    longName: 'long_name',
    phone: 'phone',
    pictureUrl: 'picture_url',
    shortDescription: 'short_description',
    signature: 'signature',
    url: 'url',
    userId: 'user_id',
    verifiedOn: 'verified_on',
    videoUrls: 'video_urls',
    phoneHref: (t: AgentType) => t.phone && toTelHref(t.phone),
    fqnPictureUrl: (t: AgentType) => (t.pictureUrl ? mediaUrl(t.pictureUrl) : undefined),
    fqnLogoUrl: (t: AgentType) => (t.logoUrl ? mediaUrl(t.logoUrl) : undefined),
    companyName: (t: AgentType) => {
      const name = t._data.company_name
      return name !== t.fullName ? name : ''
    },
    fullName: (t: AgentType) => {
      let fullname = t.longName
      if (!fullname) {
        if (t.firstName || t.lastName) {
          fullname = `${t.firstName ?? ''} ${t.lastName ?? ''}`.trim()
        }
      }
      return fullname ?? ''
    },
    avatarName: (t: AgentType) => {
      const validFullName = t.fullName.length
      const validCompanyName = t.companyName?.length

      if (validFullName) {
        return t.fullName[0]
      }
      if (validCompanyName) {
        return t.companyName[0]
      }
      // most likely this won't happen, but this user doesn't have a name
      return ''
    },
  }
) as unknown as typeof AgentWithProfile & typeof AgentWithoutProfile

export type Agent = AgentType
